import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Header from '@/components/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Facebook, Youtube, Music, Instagram, MessageCircle, Send } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function VipSmmServicesPage() {
  const navigate = useNavigate();
  const { isAdminAdil } = useAuth();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [link, setLink] = useState('');
  const [quantity, setQuantity] = useState('');
  const [ordering, setOrdering] = useState(false);

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const fetchPlatforms = async () => {
    try {
      setLoading(true);
      const response = await api.get('/vipsmm/platforms');
      if (response.data.success) {
        setPlatforms(response.data.platforms);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'Facebook': return Facebook;
      case 'YouTube': return Youtube;
      case 'Instagram': return Instagram;
      case 'TikTok': return Music;
      case 'Spotify': return Music;
      case 'Telegram': return Send;
      case 'WhatsApp': return MessageCircle;
      default: return Music;
    }
  };

  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'Facebook': return 'from-blue-600 to-blue-800';
      case 'YouTube': return 'from-red-600 to-red-800';
      case 'Instagram': return 'from-pink-600 to-purple-800';
      case 'TikTok': return 'from-black to-teal-600';
      case 'Spotify': return 'from-green-600 to-green-800';
      case 'Telegram': return 'from-blue-400 to-blue-600';
      case 'WhatsApp': return 'from-green-500 to-green-700';
      default: return 'from-orange-600 to-red-600';
    }
  };

  const openModal = (platform) => {
    setSelectedPlatform(platform);
    setSelectedService(null);
    setLink('');
    setQuantity('');
  };

  const closeModal = () => {
    setSelectedPlatform(null);
    setSelectedService(null);
    setLink('');
    setQuantity('');
  };

  const handleOrder = async () => {
    if (!selectedService || !link || !quantity) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      setOrdering(true);
      const response = await api.post('/vipsmm/order', {
        service_id: selectedService.id,
        link: link,
        quantity: parseInt(quantity)
      });

      if (response.data.success) {
        toast.success('Commande passée avec succès! 🎉');
        toast.info(`Numéro de commande: ${response.data.order_id}`);
        closeModal();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la commande');
    } finally {
      setOrdering(false);
    }
  };

  const calculateTotal = () => {
    if (!selectedService || !quantity) return '0.00';
    
    // Si Admin Adil, prix = 0$ (GRATUIT)
    if (isAdminAdil) return '0.00';
    
    // Formule: (quantité / per) * prix_base
    // per = 1000 pour abonnés/vues/likes, 100 pour commentaires
    const per = selectedService.per || 1000;
    const priceBase = selectedService.price || 0;
    
    const total = (parseInt(quantity) / per) * priceBase;
    
    // Minimum 5$
    const finalPrice = Math.max(5.0, total);
    
    return finalPrice.toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-12 w-12 animate-spin text-[#E50914]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-20">
      <Header />
      
      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Titre */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">
            Services <span className="text-[#E50914]">Boost</span> 🚀
          </h1>
          <p className="text-zinc-400 text-lg">
            Boostez votre présence sur tous les réseaux sociaux
          </p>
        </div>

        {/* Grille des plateformes - MÊME STYLE que vos services */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {platforms.map((platform) => {
            const Icon = getPlatformIcon(platform.platform);
            const color = getPlatformColor(platform.platform);
            
            return (
              <Card 
                key={platform.platform}
                className="glass border-zinc-800 hover:border-[#E50914] transition-all cursor-pointer group overflow-hidden"
                onClick={() => openModal(platform)}
              >
                <div className={`h-2 bg-gradient-to-r ${color}`}></div>
                <CardContent className="p-8 text-center">
                  <div className={`w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br ${color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <Icon size={40} className="text-white" />
                  </div>
                  
                  <h2 className="text-xl font-bold text-white mb-2 group-hover:text-[#E50914] transition-colors">
                    BOOSTER {platform.platform.toUpperCase()}
                  </h2>
                  
                  <p className="text-zinc-400 text-sm mb-4">
                    {platform.count} services disponibles
                  </p>
                  
                  <div className={`text-3xl font-bold mb-4 ${isAdminAdil ? 'text-green-400' : 'text-[#F97316]'}`}>
                    {isAdminAdil ? 'GRATUIT' : `À partir de ${platform.min_price.toFixed(2)}$`}
                  </div>
                  
                  <Button 
                    className={`w-full ${isAdminAdil ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white font-semibold py-6 text-lg`}
                  >
                    {isAdminAdil ? 'GRATUIT (Admin Adil)' : 'DÉPOSER MA DEMANDE'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>

      {/* MODAL POP-UP au centre avec 4 CASES */}
      <Dialog open={!!selectedPlatform} onOpenChange={closeModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center mb-6">
              🚀 Booster {selectedPlatform?.platform}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* CASE 1: Choix du service avec recherche */}
            <Card className="bg-zinc-800 border-zinc-700">
              <CardContent className="p-4">
                <Label className="text-white font-semibold mb-2 block">
                  1️⃣ Choix du service
                </Label>
                <Select onValueChange={(value) => {
                  const service = selectedPlatform.services.find(s => s.id.toString() === value);
                  setSelectedService(service);
                }}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
                    <SelectValue placeholder="Rechercher et sélectionner un service..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 max-h-[300px]">
                    {selectedPlatform?.services.map((service) => (
                      <SelectItem 
                        key={service.id} 
                        value={service.id.toString()} 
                        className="text-white hover:bg-zinc-700"
                      >
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedService && (
                  <p className="text-xs text-zinc-500 mt-2">
                    Prix: {selectedService.price}$ pour {selectedService.per} • Min: 100 • Max: 1M
                  </p>
                )}
              </CardContent>
            </Card>

            {/* CASE 2: Lien */}
            <Card className="bg-zinc-800 border-zinc-700">
              <CardContent className="p-4">
                <Label className="text-white font-semibold mb-2 block">
                  2️⃣ Colle ton lien ici
                </Label>
                <Input
                  type="url"
                  placeholder="https://..."
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white"
                />
              </CardContent>
            </Card>

            {/* CASE 3: Quantité */}
            <Card className="bg-zinc-800 border-zinc-700">
              <CardContent className="p-4">
                <Label className="text-white font-semibold mb-2 block">
                  3️⃣ Quantité
                </Label>
                <Input
                  type="number"
                  placeholder="1000"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white"
                  min={100}
                  max={1000000}
                />
              </CardContent>
            </Card>

            {/* CASE 4: Prix total (calcul automatique) */}
            <Card className={`${isAdminAdil ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-[#E50914] to-[#F97316]'} border-0`}>
              <CardContent className="p-4">
                <Label className="text-white font-semibold mb-2 block">
                  4️⃣ Prix Total
                </Label>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">
                    {isAdminAdil ? 'GRATUIT' : `${calculateTotal()}$`}
                  </p>
                  {isAdminAdil ? (
                    <p className="text-xs text-white/80 mt-1">
                      🎉 Compte Admin Adil - Tous les services sont gratuits
                    </p>
                  ) : (
                    <p className="text-xs text-white/80 mt-1">
                      Formule: (Quantité / {selectedService?.per || 1000}) × {selectedService?.price || 0}$ = Prix Total (Min 5$)
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Bouton commander */}
            <Button
              onClick={handleOrder}
              disabled={!selectedService || !link || !quantity || ordering}
              className={`w-full ${isAdminAdil ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700' : 'bg-gradient-to-r from-[#E50914] to-[#F97316] hover:from-[#F8212C] hover:to-[#FB923C]'} text-white font-bold py-6 text-lg`}
            >
              {ordering ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Commande en cours...
                </>
              ) : (
                isAdminAdil ? `GRATUIT (Admin Adil) - Commander` : 'COMMANDER MAINTENANT'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
